// frontend/src/auth/AuthProvider.tsx
import React, { ReactNode, useEffect } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { setTokenProvider } from "../api/baseClient"; // <- existing helper

// Runtime env injected by /env.js
declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

const env = (key: string): string | undefined => {
  const v = window.__ENV__?.[key];
  return v && v.length > 0 ? v : undefined;
};

const getAuthConfig = () => {
  // Runtime (Docker / NGINX)
  const runtimeDomain = env("AUTH0_DOMAIN");
  const runtimeClientId = env("AUTH0_CLIENT_ID");
  const runtimeAudience = env("AUTH0_AUDIENCE");
  const runtimeCallback = env("AUTH0_CALLBACK");
  const runtimeBypass = env("AUTH_BYPASS");

  // Build-time fallback (LOCAL DEV: npm start)
  const domain = runtimeDomain || process.env.REACT_APP_AUTH0_DOMAIN || "";
  const clientId = runtimeClientId || process.env.REACT_APP_AUTH0_CLIENT_ID || "";
  const audience = runtimeAudience || process.env.REACT_APP_AUTH0_AUDIENCE;
  const callbackPath = runtimeCallback || process.env.REACT_APP_AUTH0_CALLBACK || "/";
  const bypass = (runtimeBypass || process.env.REACT_APP_AUTH_BYPASS || "false") === "true";

  const redirectUri =
    callbackPath === "/" ? window.location.origin : `${window.location.origin}${callbackPath}`;

  return { domain, clientId, audience, callbackPath, bypass, redirectUri };
};

interface Props {
  children: ReactNode;
}

/**
 * Registers getAccessTokenSilently as the token provider
 * for all backend calls via baseClient.
 */
const TokenProviderBridge: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    setTokenProvider(async () => {
      try {
        const token = await getAccessTokenSilently();
        console.log("[TokenProviderBridge] token =", token ? "present" : "null");
        return token;
      } catch (err) {
        console.warn("[TokenProviderBridge] failed to get token:", err);
        return null;
      }
    });
  }, [getAccessTokenSilently]);

  return <>{children}</>;
};

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const { domain, clientId, audience, bypass, redirectUri } = getAuthConfig();

  console.log("[AuthProvider] bypass =", bypass, "env=", window.__ENV__);

  if (bypass) {
    console.log("[AuthProvider] BYPASS MODE – no Auth0Provider");
    setTokenProvider(async () => null);
    return <>{children}</>;
  }

  if (!domain || !clientId) {
    console.error(
      "[AuthProvider] Missing Auth0 configuration. /env.js must provide AUTH0_DOMAIN and AUTH0_CLIENT_ID.",
      { domainPresent: !!domain, clientIdPresent: !!clientId, env: window.__ENV__ }
    );
    // Fail open: render app without auth provider (you may choose to show an error page instead)
    setTokenProvider(async () => null);
    return <>{children}</>;
  }

  const authParams: Record<string, string> = {
    redirect_uri: redirectUri,
  };
  if (audience) authParams.audience = audience;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authParams}
      cacheLocation="localstorage"
    >
      <TokenProviderBridge>{children}</TokenProviderBridge>
    </Auth0Provider>
  );
};